import type { BlogPost } from "./posts";

/**
 * Build-log cluster: engineering and growth posts written from real work on
 * OmniCanvas during July 2026. These are dated to when the work actually
 * happened, so they use preserveDate and are excluded from the backlog
 * scheduler's date compression.
 */
export const buildLogPosts: BlogPost[] = [
  {
    slug: "shipped-concept-lines-one-toolbar",
    title: "What Shipped: Concept Lines, One Toolbar, and Fewer Wasted Downloads",
    description:
      "Concept lines, a single canvas toolbar that folds into More, checklists and wiki links in text blocks, and two fixes for downloads and reloads.",
    date: "2026-09-18",
    readTime: "5 min read",
    category: "Insights",
    preserveDate: true,
    content: `
## The Short Version

This week's release, dated September 18, 2026, has one new feature and a handful of fixes that each removed a small, repeated annoyance:

- **Concept lines**: a canvas toggle that shows which ideas run through more than one block
- **One canvas toolbar** instead of two, which folds into a More menu on narrow windows
- **Checklists and wiki links** that render properly in text blocks
- **Downloaded models that survive updates**, and **reloads that no longer load twice**

The full list is in the [changelog](/changelog).

## Concept Lines

Turn on **Concepts** at the top right of a canvas and it highlights ideas shared by text blocks and sticky notes, then joins them with a curved line, one color per concept. It matches repeated lines and shared words straight away. After an optional 23 MB download it also matches by meaning ("revenue" and "sales") using a small model that runs in the browser, so your text doesn't leave the device. The [feature walkthrough](/blog/tutorials/concept-lines-see-how-ideas-connect) covers search, the hover card with **Go to**, pinning, hiding and settings.

![A canvas with concept lines: highlighted shared words and labelled lines between blocks while hovering a sticky note](/images/blog/concepts-hover.webp)

Three things we fixed on the way to this release:

**Generic lines linked to the wrong block.** A list item like "test thing 3" copied into another block was linked to whichever block happened to be nearest, because "thing" and numbers are filler and only "test" matched. Short lines that repeat word for word are now their own kind of concept, outrank the words inside them, and link to the exact line they repeat.

**Too much at once.** With several concepts on a canvas, every line competing for attention made the canvas hard to read. Lines are now thin and faint at rest and come forward only for the block you hover or edit. Labels on crossing lines used to stack on top of each other; each label now slides along its own line to a clear spot, or is left off.

**Lines crossed text.** A line running across a block made the text look struck through. Lines now go under blocks and meet each block on the side facing the other one.

Lines and highlights are drawn on top of the canvas and never written into your notes, so saving and sync are unaffected and nothing about concepts is stored or uploaded. The limits: one canvas at a time, at most eight concepts (one per color), and the button is hidden on windows narrower than 1,024 pixels, which includes phones.

## One Toolbar Instead of Two

The canvas used to have a viewport bar in the bottom-left corner and a tool dock in the centre. They are now one draggable bar, grouped left to right: history and zoom, drawing tools, style, insert and AI, view.

![The single canvas toolbar with its zoom popover open: minus, slider, plus, Fit, 100% and Zoom to selection](/images/blog/concepts-toolbar-zoom.webp "Zoom collapses to one button with a popover.")

![The View menu open from the toolbar: Sections, Outline, Minimap and Dark page toggles](/images/blog/concepts-toolbar-view.webp "View toggles live in one menu.")

Zoom collapses to one percentage button with a slider, Fit, 100% and Zoom to selection. The view toggles (sections, outline, minimap, dark page) live in one View menu.

The part we're happiest with is narrow windows. Before, when the toolbar was wider than the canvas, the end of it scrolled out of sight behind a hidden scrollbar, so tools just seemed to be missing. Now whole groups fold into a **More** menu, view first, then insert and AI, then style. Nothing disappears; it moves one click away. On mobile, Draw is now directly in the toolbar, one tap away.

## Checklists and Wiki Links in Text Blocks

AI meeting captures write action items as markdown checkboxes. The rich text blocks didn't understand that syntax, so each item showed up as a bullet with literal brackets in front of it. Checklists now render as real checkboxes.

Wiki links had the same problem: a saved link showed as raw bracket text in text blocks and sticky notes. Now it shows the note's title and opens the note when you click it. How links are saved is unchanged.

Also fixed: meeting-capture panels now sit directly under their text instead of at the bottom of the block, and the graph opens framed below its controls instead of with nodes hidden under the control bar.

## Two Fixes That Stopped Wasting Your Time

**Models downloaded again after every update.** When the app updates, it clears its old cached files. The cleanup removed every cache with "omnicanvas" in its name, and that included the caches holding the on-device speech model and the new concept model. So every release quietly meant another download. Model caches are now kept; the rest of the old app files are still cleared. A related bug made a single failed model load (a busy tab, say) forget that the concept model was downloaded, and ask for the 23 MB again. Now it only asks if the files are actually gone; otherwise it offers **Try again** and reuses them.

**Quick reloads loaded twice.** Startup was only marked as finished by a five-second timer. Any reload within five seconds, including a manual refresh or the app's own update reload, looked like a startup that had crashed. The next load then ran crash recovery, cleared the offline cache and reloaded again, so you sat through "Opening your notes..." twice. Startup is now marked finished once your notes have loaded and the real interface is on screen. The timer stays as a fallback, and a startup that really does fail is still recovered.

Neither showed up as an error. Both just made the app feel slower than it is.

## What's Next

If you use concept lines and something links that shouldn't (or doesn't that should), [tell us](/contact). For how to use them as part of a broader habit, see [How to Find Hidden Connections in Your Notes](/blog/knowledge/find-hidden-connections-in-your-notes).
`,
  },
  {
    slug: "what-60-signups-taught-us-about-activation",
    title: "60 Signups, 74% Activation, 8% Week-One Retention: What Our Own Data Actually Said",
    description:
      "We pulled a month of production data on every new account. Activation was strong, the signup form fine, and retention bad. Here is the whole picture.",
    date: "2026-07-28",
    readTime: "9 min read",
    category: "Insights",
    preserveDate: true,
    coverImage: null,
    content: `
## We Went Looking for a Signup Problem and Found Something Else

At the end of July we had about 60 new accounts for the month. That is a small number, and small numbers are the best time to look closely, because you can still reconcile every single row by hand.

So we did. We queried production for aggregate account metadata — creation timestamps, referrer, whether a note was ever created, what kinds of objects it contained, and when it was last edited. We did not read note titles or note content. The whole point was to answer a product question, not to browse anyone's notes.

We expected to find a leaky signup form. Instead we found that the signup form was one of the healthiest parts of the funnel, and that the real problems were on either side of it.

## Reconciling the Number First

The dashboard said 60. The database said 58 rows. After removing one automated health-check account, we could reconcile 57 real signups in Pacific time, or 58 if you draw the boundary in UTC.

This sounds pedantic. It is not. If you cannot explain the gap between two of your own numbers, you cannot trust any conclusion you draw from either of them. Timezone boundaries and monitoring accounts are the two most common reasons a growth dashboard disagrees with a database, and both were in play here.

Working number: 57.

## Activation Was Genuinely Good

Of those 57 accounts:

- 45 signed up with Google, 12 with a password
- 42 created a real note beyond the welcome canvas — **74% activation**
- 38 of those 42 did it within five minutes of signing up
- 23 created at least two notes; seven created five or more
- The median user created exactly one note

Seventy-four percent activation is a number most products would be happy with. And the five-minute detail matters: people who activate at all do it almost immediately. There is no slow-burn cohort quietly warming up. You either get someone in the first five minutes or you do not get them.

What did they actually do on the canvas?

- 26 used canvas objects — mostly text, rectangles, images, and freehand drawing
- 23 used markdown blocks
- 12 used sticky notes
- Only seven touched the AI or media features
- Four used folders, two used tags, one collaborated, one saved a recording

That last group is the interesting one. The features we talk about most in marketing — AI meeting notes, recording, collaboration — were used by almost nobody in their first session. New users draw boxes and write text. Everything else is a second-week behavior, if it happens at all.

## Retention Was Not Good

Here is the part that actually mattered.

- 7 of 41 eligible activated users edited a real note after day one — **17%**
- 2 of 25 eligible users edited one after day seven — **8%**
- Only two people created notes on more than one calendar day

These are deliberately conservative, lower-bound figures. They measure saved content changes, not opening the app, not reading an existing note, not scrolling around a canvas. Someone could have opened OmniCanvas every day for a week to reread their meeting notes and would not appear in these numbers at all.

Even granting that, the shape is unmistakable. People arrive, they get value once, and they do not come back to build on it. A note-taking app that gets used once is not a note-taking app. It is a scratchpad.

## Where Signups Came From, and Which Ones Were Worth Having

| Source | Signups | Created a note |
|---|---|---|
| Google | 36 | 67% |
| ChatGPT | 10 | 90% |
| Direct / unattributed | 7 | 71% |
| DuckDuckGo | 2 | 100% |
| Bing | 1 | 100% |
| Claude | 1 | 100% |

Google sends the most people. ChatGPT sends the best people. Ten signups from ChatGPT produced a 90% activation rate, and 60% of them created multiple notes, against 39% for Google traffic.

The sample is tiny and we are not going to over-read it. But the mechanism is plausible: someone who asks an assistant "what should I use for spatial note-taking" and gets a recommendation arrives with intent already formed. Someone who lands on a listicle from a search result is still shopping.

## The Actual Leak

Then we looked at the marketing site. About 11,668 distinct visitors over the period.

- **Homepage:** 433 sessions, 59 sessions with a CTA click — **13.6% clickthrough**
- **Blog posts:** 11,001 sessions, 21 sessions with a CTA click — **0.19% clickthrough**
- **Signup screen:** roughly 70 recorded actors produced 57 accounts
- **Password signup:** 12 completions out of 13 attempts

Read those numbers next to each other. The homepage converts at seventy times the rate of the blog. The signup form converts at over 80%. And the blog — which is where essentially all of our traffic lives — converts at almost nothing.

Individual posts made it worse:

- **/best-whiteboard-apps/** — 1,824 sessions, zero CTA clicks, zero attributed signups
- **/omnicanvas-vs-obsidian-canvas/** — 866 sessions, zero clicks, zero signups
- **/best-free-infinite-canvas-apps/** — only 365 sessions, but nine signups and eight activations

Our single highest-traffic article produced nothing. An article with a fifth of the traffic produced nine accounts. The difference is not traffic quality in some mystical sense. It is that one of those articles asks the reader to do something they were already trying to do, and the other one ends with a paragraph about how there are many good options.

There is one hopeful signal: blog CTR reached 1.21% during July 22–28, up from 0.07% earlier in the month. Recent CTA work may already be helping. That needs another two weeks of clean measurement before we believe it.

## What We Concluded

Three things, in priority order.

**1. The blog-to-product transition is the whole ballgame.** If the 11,001 blog sessions converted at even the homepage's 13.6%, the arithmetic gets absurd. We do not need more traffic. We need the traffic we have to be able to do something at the end of the article.

**2. The signup form is not the problem, so stop optimizing it.** Twelve of thirteen password attempts completed. There is nothing left to win there, and every hour spent on it is an hour not spent on retention.

**3. Retention is the real product problem.** Activation at 74% and week-one retention at 17% describes a product that demonstrates its value and then fails to become a habit. No amount of funnel work fixes that. It is a product question about what brings someone back on day two.

## What We Did About It

The immediate change was to stop sending blog readers to a signup wall. If an article is about whiteboarding, the link at the end should open a whiteboard — the real one, no account — and ask for an account later, once there is something worth saving.

We shipped that on July 28, along with intent-specific CTAs on the highest-traffic whiteboard and Obsidian articles, meaningful-work detection, save-your-work prompts, and a second-note loop that waits until someone has actually done something before suggesting what to do next.

We also wrote down the whole funnel and a reusable cohort query so this analysis can be rerun in one command instead of one afternoon. That turned out to be the most valuable artifact of the day.

We will publish the after-numbers. Including if they are bad.

If you want to see what the reader now lands on instead of a signup form, [open a blank canvas](https://app.omnicanvasnotes.com/?mode=try&template=brainstorm-web&utm_source=blog&utm_medium=organic&utm_campaign=activation_teardown) with no account required.

30 days free. No credit card required.
`,
    cta: { templateId: "brainstorm-web", templateName: "Brainstorm Web" },
  },

  {
    slug: "we-removed-the-signup-wall",
    title: "We Removed the Signup Wall and Let People Use the App First",
    description:
      "Our blog converted at 0.19%. The fix was not a better call to action — it was deleting the account requirement and asking after someone made something.",
    date: "2026-07-29",
    readTime: "8 min read",
    category: "Insights",
    preserveDate: true,
    coverImage: null,
    content: `
## The Setup

We had just finished a full teardown of a month of production data. The headline: our blog drew 11,001 sessions and converted at 0.19%, while our homepage converted at 13.6%. Activation for people who did sign up was a healthy 74%, and week-one retention was a grim 8%.

Two problems, then. Getting readers into the product, and getting users to come back. This post is about the first one.

## Why "Add a Better CTA" Was the Wrong Answer

The obvious move when a page converts badly is to make the button better. Stronger verb, contrasting color, move it higher, add one above the fold and one below.

We had already done a round of that. It helped a little — CTR moved from 0.07% to 1.21% in the last week of July, which sounds enormous until you notice it is 1.21%.

The reason it stayed small is that the button was not the obstacle. What sat behind the button was the obstacle. Every marketing CTA deep-linked to the app with a signup parameter attached. Somebody reading an article about whiteboard apps, mildly curious, clicked through and got an account creation screen.

That is a terrible trade to offer a stranger. They have invested four minutes of reading. We are asking for an email address, a password, and a commitment, in exchange for a product they have not seen. The button was fine. The offer was bad.

## The Change

We made the real app openable without an account.

Not a demo. Not a video. Not a stripped-down sandbox that hints at what the product might do. The actual application, with the actual canvas, opened directly from the article, with the toolbar live and the tools working.

Concretely:

- Marketing CTAs no longer force a signup mode parameter
- Article links can specify a template, so a whiteboarding article opens a whiteboard and a meeting-notes article opens the meeting-notes template
- Work is held locally until the user chooses to save it
- The account prompt appears **after** meaningful work exists, not before

The last point is where most of the design effort went.

## Defining "Meaningful Work"

The prompt to create an account has to fire at the right moment. Too early and it is the signup wall again with extra steps. Too late and someone closes the tab with unsaved work, which is worse than never having engaged them.

Our first version counted objects and text length. Draw enough shapes or type enough characters and you are deemed to have done meaningful work.

That heuristic is weak, and we flagged it as the thing most likely to need revision. Object count rewards fidgeting. Someone who drags five rectangles around while deciding whether they like the tool registers as more invested than someone who wrote two sentences that actually matter to them. Character count has the mirror problem.

The direction we prefer is intent-shaped rather than volume-shaped: did this person do something that implies they want to keep it? Naming a note. Structuring content into a hierarchy. Recording audio. Returning to a canvas after navigating away. Those are cheap to detect and much harder to trigger by accident.

We shipped the volume heuristic because it was ready, and we wrote down why it is wrong so that the next version has somewhere to start.

## The Second-Note Loop

The other half of the change targets retention rather than acquisition, and it comes from the same finding: the median new user creates exactly one note.

One note is a trial. Two notes is a beginning of a habit. So there is an intent-aware prompt that waits until someone has finished a first piece of real work, then suggests a specific next thing based on what they just did — not a generic "create another note" nudge, and not a tour.

The rules we gave it:

- Never fire before meaningful work exists
- Never fire more than once per session
- Suggest something concrete and related to what they just made
- Be dismissible permanently, in one click, with no follow-up

Anything that violates the last rule stops being onboarding and starts being nagging.

## What Else Had to Change

Removing a signup wall touches more than the wall.

**Positioning.** The homepage led with features. It now leads with visual and spatial notes, because that is what the product is and it is what the highest-converting article was about.

**A meeting-notes landing page.** We ship a full AI meeting-notes suite and had no page for it. The AI-notes blog cluster had nowhere to send people.

**Attribution that survives a subdomain hop.** The marketing site and the app are different origins. Without a shared anonymous identity, every click from the blog into the app looked like direct traffic and every conversion was unattributable. We added a cross-subdomain anonymous identifier, first-touch attribution, signup joining, and a small set of privacy-minimal funnel events. Without this, we would ship the change and have no way to know whether it worked.

**Lifecycle email deep links.** The existing emails pointed at generic entry points. They now land where the message is about.

## The Trap We Had to Handle

There is a specific failure mode in try-before-signup that quietly loses user data.

Somebody uses the product anonymously, makes something, then clicks a save or signup action. If the handoff into the authenticated account serializes only part of what they made, they will believe their work was saved when some of it was not.

In our case, canvas elements serialize cleanly, but things like recordings are sidecar data attached alongside the canvas rather than inside it. A naive handoff carries the drawing and silently drops the audio.

The rule we settled on: either the handoff carries everything, or the action is labeled precisely enough that nobody is surprised. "Save canvas" when only the canvas is going. Never "Save my work" when part of the work is going to disappear.

Losing a stranger's first ten minutes of work is the single most expensive bug in a try-before-signup flow, because that person never comes back and never tells you.

## How We Will Know If It Worked

The before-state is measured and written down: 0.19% blog CTR, 74% activation, 17% day-one retention, 8% day-seven retention, 57 signups in the month.

The measurement plan is boring on purpose:

- Two clean weeks before drawing any conclusion
- Compare blog CTR against the homepage's 13.6%, which is the realistic ceiling
- Watch activation for anonymous users separately from account-first users
- Track the second-note rate as the leading retention indicator

We will publish the results either way. A post about a growth change that only appears when the change worked is not a data post, it is an advertisement.

Want to see what a blog reader now gets instead of a signup form? [Open the canvas](https://app.omnicanvasnotes.com/?mode=try&template=brainstorm-web&utm_source=blog&utm_medium=organic&utm_campaign=signup_wall) — no account.

30 days free. No credit card required.
`,
    cta: { templateId: "brainstorm-web", templateName: "Brainstorm Web" },
  },

  {
    slug: "ai-wrote-lies-about-our-own-product",
    title: "Our AI-Written Competitor Comparisons Were Lying About Our Own Product",
    description:
      "We generated a cluster of alternatives posts with AI, then read them. Eleven denied features OmniCanvas ships. The failure mode nobody warns you about.",
    date: "2026-07-06",
    readTime: "8 min read",
    category: "Insights",
    preserveDate: true,
    coverImage: null,
    content: `
## The Failure Mode Is Not What You Expect

Everyone worries that an AI writing about your product will overclaim. It will invent an integration, promise a feature you do not have, or hallucinate a pricing tier. That is the risk people plan for, and it is a real one.

The failure we actually hit was the opposite, and it was much harder to catch.

We generated a cluster of competitor "alternatives" articles. Miro alternatives, Excalidraw alternatives, FigJam alternatives, Milanote, tldraw, Apple Freeform, Whimsical, Mural, Lucidspark. Standard comparison content. The drafts read well. The structure was right, the competitor descriptions were accurate, the tone was reasonable and not overly promotional.

Then we read them against the actual product, and found that across eleven posts, the articles were confidently and repeatedly **understating what we ship**.

## What the Articles Claimed

A representative sample of what we had published about ourselves:

- The Miro alternatives post framed OmniCanvas as "not a workshop host" that "lacks real-time multiplayer"
- The FigJam alternatives post described it as a solo, private notebook
- The Lucidspark post drew a line between "personal tools" and "team suites" and put us firmly in the personal column
- The Mural post advised readers to pick us only "if you do not need live facilitation"
- The Excalidraw and tldraw posts routed every collaboration requirement to Miro and FigJam
- The Milanote post did not list us among collaborative creative canvases at all

OmniCanvas has real-time collaboration. Live cursors, permissions, invites, up to 25 simultaneous collaborators.

We had written, published, and were actively driving search traffic to eleven articles telling prospective users that we could not do the thing we do.

## Why This Happens

The mechanism is straightforward once you see it, and it generalizes to any product.

A language model writing about Miro is on solid ground. Miro is enormous, well documented, discussed in thousands of articles, and its capabilities are effectively common knowledge. The model has a strong, accurate prior.

A language model writing about your product has almost nothing. You are small. Your docs may not be in the training data. What the model has instead is a category prior: *a small independent canvas app made by a tiny team.* And it knows what those are usually like. They are usually single-player. They usually lack real-time sync, because real-time sync is hard and expensive and small teams usually skip it.

So it fills the gap with the category average. And the category average, for a small app, is "no multiplayer."

This produces text that is fluent, plausible, appropriately hedged, and wrong. It does not read like a hallucination. It reads like sober analysis. That is exactly what makes it dangerous — an overclaim trips your internal alarm immediately, and a modest, reasonable-sounding underclaim slides right past.

## The Second Version of the Same Problem

We found the mirror image in a different cluster, and it is worth describing because the correct fix went the other direction.

Our AI meeting-notes posts repeatedly described OmniCanvas encryption as blanket and always-on. Phrases like "AES-256 zero-knowledge encryption," "end-to-end encrypted," "encrypted storage," "encrypted notes," used as general descriptions of the product.

That is not how it works. Encryption is opt-in, per note or per folder. A user who reads "your notes are end-to-end encrypted," assumes it applies to everything, and stores something sensitive in a note they never explicitly encrypted has been actively misled by us.

Same root cause, opposite direction. The model knew that privacy-focused note apps in this category tend to advertise blanket encryption, so it wrote the category convention rather than our specific behavior.

Underclaiming collaboration costs you signups. Overclaiming encryption is a trust and potentially a legal problem. The second one is much worse.

## What We Actually Fixed

We corrected eleven posts across three files. The pattern of each fix:

| Post | The false claim | The correction |
|---|---|---|
| Miro alternatives | "Not a workshop host, lacks real-time multiplayer" | Live cursors, permissions, invites, 25 collaborators; Miro's real edge is enterprise facilitation |
| Excalidraw alternatives | All collaboration routed to Miro/FigJam | OmniCanvas listed as a genuine collaborative option |
| FigJam alternatives | "Solo private notebook only" | Collaboration is real; distinguished from workshop facilitation tooling |
| Mural alternatives | "If you do not need live facilitation" | Collaboration yes, enterprise facilitation no |
| Lucidspark alternatives | Personal-only vs team-suite framing | Corrected, collaboration details added |
| Milanote / tldraw / Freeform / Whimsical | Omitted from collaborative options | Added, with honest scope |
| iPad canvas post | Native iPad app and on-device AI implied | Replaced with accurate PWA and web positioning |
| AI notes cluster | Blanket "end-to-end encrypted" | Opt-in per note or folder, stated plainly |

Note what the corrections are *not*. We did not turn "OmniCanvas cannot do collaboration" into "OmniCanvas is better than Miro at collaboration." Miro genuinely is better at facilitated enterprise workshops, and the corrected posts say so. The goal was accuracy in both directions, and the honest version of the Miro comparison still recommends Miro for a large chunk of readers.

## What We Changed About the Process

**Every claim about our own product needs a source in the codebase or the docs.** Competitor claims can come from public material. Self-claims cannot come from the model's priors, because the model does not have any.

**Give the model a capability sheet, not a product name.** If the draft is generated from an explicit, current list of what ships — including scope and limits like "encryption is opt-in per note" — the category prior has nothing to fill in.

**Read for what is missing, not just what is wrong.** Every one of these errors was an omission or a hedge. Proofreading catches false statements. It does not naturally catch a true-sounding sentence that quietly excludes you from a list you belong on.

**Audit the highest-traffic posts first.** One of the affected articles was drawing well over a thousand sessions a month and producing zero signups. We had assumed the CTA was weak. Part of the problem was that the body text told readers we could not do what they came to do.

## The Thing Worth Remembering

If you use AI to write about your own product, it will describe the category you appear to belong to, not the product you actually built. Where you are typical, that works. Where you are unusual — where you did the hard thing a small team normally skips — it will quietly erase your best differentiator and sound perfectly reasonable doing it.

Check the places where you are exceptional. Those are exactly the sentences that will be wrong.

If you want to see the collaboration we spent eleven articles denying, [open a shared canvas](https://app.omnicanvasnotes.com/?mode=try&template=brainstorm-web&utm_source=blog&utm_medium=organic&utm_campaign=ai_wrote_lies) and send someone the link.

30 days free. No credit card required.
`,
    cta: { templateId: "brainstorm-web", templateName: "Brainstorm Web" },
  },

  {
    slug: "browser-based-meeting-recorder-whisper-webgpu",
    title: "A Private Meeting Recorder That Runs Entirely in a Browser Tab",
    description:
      "We built recording, Whisper transcription and AI summaries with no server. Two problems nearly sank it: GPU memory and the cross-origin model cache.",
    date: "2026-07-29",
    readTime: "10 min read",
    category: "Techniques",
    preserveDate: true,
    coverImage: null,
    content: `
## The Idea

Our public demo page let visitors draw on a canvas. The welcome panel promised "record audio with transcription," which the demo could not actually do. That gap had been sitting there for a while.

The obvious fix was to wire the demo up to our production transcription backend. We did not want to do that, for two reasons. First, an unauthenticated endpoint that accepts arbitrary audio and returns transcripts is a service you are now operating for the entire internet. Second, and more importantly, it would waste the actual opportunity.

The interesting version is a recorder where the audio never leaves the browser. Not "we promise not to look." Structurally incapable of uploading, because there is nothing to upload to.

Here is what that took.

## The Architecture

Three pieces, all client-side.

**Capture.** MediaRecorder for microphone audio. This is the boring, well-supported part — MediaRecorder has broad browser support and has for years.

**Transcription.** Whisper running in the browser through Transformers.js, in a worker so the canvas stays interactive. WebGPU when available, WebAssembly as the fallback. The fallback is not optional. WebGPU support is still uneven across browsers and devices, and a recorder that only works for some visitors is worse than no recorder, because the failure is invisible until someone has already recorded five minutes of audio.

**Text generation.** For summaries and action items, a second model — WebLLM running a roughly 0.9 GB Llama variant.

Our production app already had most of this: a Whisper worker, a persistent model cache, WebGPU/WASM routing, and cleanup logic. The correct move was to extract the reusable local-ASR pieces into a shared package rather than write a second transcription implementation for the demo. Two implementations of the same hard thing means two sets of bugs and one of them is always the stale one.

## Problem One: You Cannot Hold Both Models at Once

This is the finding that cost us the most time and is the least documented anywhere.

Whisper loads into GPU memory. It transcribes. Then the user clicks "Summary," and WebLLM tries to load a 0.9 GB model into GPU memory.

If Whisper is still resident, you exceed available VRAM on machines that could comfortably run either model individually. The failure is not graceful. It surfaces as an allocation error, or a hang, or in some configurations the browser tab simply dying — and it happens on perfectly capable laptops, which makes it look random.

The fix is explicit: **release Whisper from GPU memory after transcription completes, before loading the text model.** Not "let garbage collection get to it eventually." An explicit disposal, awaited, before the next load begins.

This means the pipeline has a real state machine rather than two independent async loads:

1. Load Whisper
2. Transcribe
3. Dispose Whisper, await confirmation
4. Load WebLLM on first text action
5. Run text actions, keeping the model resident for subsequent ones

Step three is the one everybody skips, and you will not catch it in testing on a workstation with plenty of VRAM.

## Problem Two: The Cross-Origin Model Cache

Our marketing site and our app run on different origins. The demo lives on the marketing site. The production recorder lives in the app.

Browser storage is partitioned by origin. So the model cache does not carry over.

A visitor downloads the Whisper model on the demo, is impressed, signs up, opens the app — and downloads the same model again. Hundreds of megabytes, twice, for the same person, in the same session, on the same machine.

There is no clever workaround. This is the correct security behavior and it is not going to change. The options are real architectural choices:

- **Accept it and minimize the cost.** Use Whisper Tiny in the demo. The download is small enough that duplicating it is annoying rather than disqualifying.
- **Host the demo experience on the app origin** and launch or embed it from the marketing page, so the cache is shared.

We took the first option for the initial version because it ships in days rather than weeks. But if converting demo users into app users is the primary goal, the second is the right answer, and the decision should be made deliberately rather than discovered later.

The general lesson: if your product involves a large client-side model download, your origin layout is a product decision, not an infrastructure detail.

## Making It Spatial Instead of a Widget

The first design put recording in a panel — a dock with a waveform, a timer, and a transcript pane. Functional, and completely beside the point.

The whole argument for OmniCanvas is that a transcript belongs on a canvas next to the diagram you drew during the meeting, not in a separate transcript inbox. A demo that puts the transcript in a side panel demonstrates the opposite of our thesis.

So the demo produces a **recording card on the canvas**. It appears near the visible center when recording starts. It shows live status. It fills with timestamped transcript lines that seek playback when clicked. It drags, resizes, renames, and deletes. It pans and zooms with the canvas, because it uses the same transformed HTML overlay as the production app rather than a demo-specific imitation.

Reusing the production card meant the demo inherited months of fixes for drag behavior, playback, transcript seeking, and zoom interaction. Building a lookalike would have meant rediscovering all of them.

## The AI Actions, and the Ones We Hid

The demo supports summary, action items, key decisions, meeting minutes, standup recap, agenda, entity extraction, custom prompts, and mind maps generated as real canvas elements. There is a local transcript chat scoped to the recording.

We deliberately hid two production actions:

- **Identify Speakers**, because we have no local acoustic diarization. Offering it would produce a confident guess dressed up as an analysis.
- **Find Related Notes**, because a demo visitor has no notebook to relate anything to.

An action that technically returns output but cannot honestly work is worse than a missing action. It teaches the visitor that our AI features produce plausible nonsense, which is the exact opposite of the impression the demo exists to create.

## The Privacy Design, Stated Honestly

Recording and transcription are always in-browser. For the text actions, we offer a choice on first use:

- **On this device** — downloads the browser model, nothing leaves the machine, maximum privacy
- **OmniCanvas-hosted** — sends the transcript, never the audio, to our own hardware, faster and higher quality

The rules we set for the hosted path, because "we host it ourselves" is a claim that has to survive contact with the implementation:

- Cloud failover disabled. Our routing layer supports silent failover to third-party providers by default. For this route it is off, and responses marked as cloud-served are rejected server-side rather than trusted.
- Response caching disabled. The optional cache would store transcript content in shared storage.
- Owned nodes only, metadata-only logging.
- Same-origin validation, size limits, rate limiting backed by the database, timeouts.
- The client sends **action IDs**, not arbitrary prompts. Otherwise the demo is a free general-purpose LLM endpoint with our name on the bill.

And in the interface: a persistent badge showing which provider is active, and **never silently switching between them**. If the on-device model fails, the demo says so. It does not quietly send the transcript to a server while displaying a privacy promise. That single behavior is the difference between a privacy feature and a privacy claim.

## What Shipped

Spatial recording cards, in-browser Whisper transcription, playback, timestamps, resize, and downloads for both audio and transcript. Nine AI actions with explicit provider choice. Mind maps as real canvas elements. Warnings before leaving with an undownloaded recording.

The clearest thing we learned: the browser is a genuinely viable runtime for this. The constraints are not CPU or model quality. They are **GPU memory** and **origin boundaries** — two things that barely come up in server-side machine learning and that will decide whether your in-browser feature works.

[Try the recorder on the demo canvas](https://app.omnicanvasnotes.com/?mode=try&template=meeting-notes&utm_source=blog&utm_medium=organic&utm_campaign=browser_recorder) — no account, no upload.

30 days free. No credit card required.
`,
    cta: { templateId: "meeting-notes", templateName: "Meeting Notes" },
  },

  {
    slug: "one-canvas-four-modes",
    title: "One Canvas, Four Modes: How We Added Slides and Design Without Splitting the Product",
    description:
      "Notes, Whiteboard, Slides and Design are four separate tools. We built them as four lenses over one canvas — the hard part was what switching must not do.",
    date: "2026-07-30",
    readTime: "9 min read",
    category: "Insights",
    preserveDate: true,
    coverImage: null,
    content: `
## The Question That Started It

"You know how Figma lets you build a series of slides on a canvas. How hard would that be for us?"

The honest first answer was: adding a slide feature is not hard. Adding it *well* is hard, because our toolbar was already crowded and slides would become one more button lost among thirty others.

That reframed the problem. The question was not "how do we add slides." It was "how do we let one canvas serve four genuinely different jobs without becoming unusable at all four."

## The Principle

We landed on one rule that decided every subsequent design question:

> **Modes are lenses over the same canvas, not different document types.**

A user can switch modes at any time, on any note, without converting, exporting, or losing anything.

This is a stronger constraint than it sounds. It rules out the obvious implementation — a slide deck as a distinct document kind with its own storage and editor — which is what most products do and which produces the familiar misery of "this is a whiteboard, you cannot put slides in it, please create a new file."

The four modes, mapped to what a person is actually doing:

| Mode | The promise | Primary interface | Main output |
|---|---|---|---|
| Note | Capture and understand | Calm document view | Notes, Markdown, PDF |
| Whiteboard | Think spatially | Fast infinite canvas | Board, diagram, section |
| Slides | Tell a story | Ordered frames and presenter | Deck, PDF, slide images |
| Design | Polish an artifact | Artboards, layers, inspector | PNG, SVG, PDF assets |

Read down the first column: **Capture, Think, Present, Polish.** That is one workflow, not four products. Take meeting notes, spatially organize them, turn the good ideas into slides, polish a diagram for the deck — without copying anything anywhere.

## The Hard Part Is the Negative Space

Most of the design work went into specifying what switching modes must *never* do. Every one of these was a real bug risk:

- **Never duplicate, convert, hide, or delete content.** If you switch to Slides and your loose sticky notes vanish because they are not on a frame, the feature is broken. Content outside the current mode's primary abstraction stays visible.
- **Never create an undo entry.** Mode is a view preference. If switching modes is undoable, then undo after a mode switch does something surprising, and undo is the one interaction that must never surprise anyone.
- **Never remount the editor.** Remounting the canvas loses viewport, selection, and in-progress edits, and produces a visible flash. The canvas stays mounted; only the surrounding interface changes.
- **Never force collaborators into the same mode.** Mode is per-user local state. Two people can edit the same scene simultaneously with one in Slides and one in Design. This falls out of storing mode locally rather than in the shared document, and it is the single decision that keeps the feature from fighting with real-time collaboration.
- **Never lose position.** Each mode remembers its own: Note scroll position, Whiteboard camera, selected slide, Design camera. Switching away and back returns you to where you were, not to origin.

What switching *does* change: the toolbar, the side panels, the defaults for new objects, camera behavior, and the export options.

## Why This Fit Our Existing Architecture

We got lucky, in the sense that a decision made a year earlier turned out to be the right foundation.

Our Focus view already presented the same underlying content as a clean document while leaving the canvas mounted underneath. That is exactly the mode pattern, built once for a different reason. And most of the tools the four modes needed already existed in the toolbar — the work was organizing them, not building them.

That is why the mode framework itself was a small piece of work while Slides and Design were substantial. The framework was mostly making an existing pattern general.

Existing sessions map cleanly: Focus becomes Note mode, Canvas becomes Whiteboard mode. Nobody's habits break.

## The Selector

One compact control in the header: **Note | Whiteboard | Slides | Design.**

- Desktop: a four-part control with icon, label, and a one-line tooltip
- Mobile: shows the current mode; tapping opens a bottom sheet
- Keyboard: Cmd/Ctrl + Shift + 1 through 4
- Every mode and every tool stays reachable from the command palette
- Mode is remembered per user, per note
- New blank notes start in Note mode; templates can recommend otherwise — Meeting Notes opens in Note, Brainstorm opens in Whiteboard, Pitch Deck opens in Slides

## The Naming Decision

We nearly called the fourth mode "Designer." We did not, and the reasoning generalizes.

"Designer" implies Figma: full layers, components, constraints, auto-layout, prototyping, shared libraries. If we ship a mode called Designer and it has artboards and an inspector but no components, every designer who tries it concludes the feature is half-built — even if what is there works well.

A name sets an expectation, and an expectation you cannot meet turns a good feature into a disappointing one. We shipped Design mode with a scope we could actually deliver: artboards, layers, a geometry and style inspector, alignment, distribution, z-order, presets, and PNG/SVG/PDF export. Real, useful, and not pretending to be Figma.

The same logic applied to Slides. It is called Slides, not Presentations, and it does what the word promises: frames, thumbnails, reordering, speaker notes, presentation mode, and export.

## The Rollout

We staged it deliberately:

1. Introduce the mode selector and reorganize existing tools into Note and Whiteboard
2. Confirm switching feels natural and does not confuse anyone
3. Build Slides on the existing frames foundation
4. Add Design only once we could genuinely support it

Step two matters. If the mode concept itself is confusing, adding two more modes multiplies the confusion. Better to find that out with the two modes people already had.

## The Result

Four modes over one persistent canvas. Frames tagged as slides, artboards, or sections. Per-user, per-note mode preference and viewport. Collaboration, encryption, templates, responsive layouts, accessibility, and legacy-note migration all integrated rather than bolted on.

The broader lesson, if there is one: **when a product needs to do several different jobs, the choice between one flexible tool and several specialized ones is usually a false choice.** One powerful substrate with progressive disclosure gets you both, and the engineering discipline it demands is almost entirely in what you refuse to let the mode switch touch.

[Open a canvas and try switching modes](https://app.omnicanvasnotes.com/?mode=try&template=brainstorm-web&utm_source=blog&utm_medium=organic&utm_campaign=four_modes) — no account required.

30 days free. No credit card required.
`,
    cta: { templateId: "brainstorm-web", templateName: "Brainstorm Web" },
  },

  {
    slug: "open-source-design-canvas-comparison",
    title: "We Evaluated Every Open-Source Design Canvas. Here Is What We Found.",
    description:
      "OpenPencil, Penpot, tldraw, Excalidraw and Apple Freeform, assessed for embedding in a React app. Licensing, architecture, and the catch in each one.",
    date: "2026-07-30",
    readTime: "9 min read",
    category: "Reviews",
    preserveDate: true,
    coverImage: null,
    content: `
## Why We Were Looking

We wanted to add a design mode to OmniCanvas — artboards, layers, an inspector, precise vector editing. The kind of thing Figma does.

Building that from scratch is a multi-year project. So the first question was whether an existing open-source canvas engine could be embedded inside our React application to provide it.

We evaluated the serious candidates. Here is the summary, then the detail.

| Project | Best use | Verdict |
|---|---|---|
| **OpenPencil** | Design mode | Best candidate |
| **Excalidraw** | Notes, Whiteboard, Slides | Keep as the existing engine |
| **Penpot** | A complete standalone design platform | Too heavy to embed |
| **tldraw** | General canvas SDK | Not truly open source for production |
| **Apple Freeform** | Whiteboard UX benchmark | Closed source; study, do not reuse |

## OpenPencil

The strongest candidate, and it was not close.

What makes it unusually well suited to embedding:

- MIT licensed, which is the license you want when the thing is going inside a commercial product
- A framework-independent core package, so the editing engine is not welded to a specific UI framework
- An SDK explicitly designed for embedding custom editing surfaces, rather than an application that happens to have an API
- Genuinely deep feature coverage: frames, pages, layers, vector paths, auto layout, components, variables, export, and a degree of Figma file compatibility
- CanvasKit/Skia rendering with Yoga for layout — a serious, performant foundation

**The catch, and it is a real one:** the supplied UI SDK is Vue, and OmniCanvas is React. That leaves two options. Mount a small Vue surface inside React, which works but means shipping a second framework and owning the boundary between them. Or build a React adapter over the framework-independent core, which is more work up front and much cleaner afterward.

**The second catch:** OpenPencil's own roadmap is candid that prototyping, comments, shared libraries, and full Figma fidelity are incomplete. Presentation and whiteboard-style features are not first-class. If you need those, you are building them.

For a design mode specifically — artboards, vectors, layers, precise editing — it is the right foundation. For a general canvas, it is aimed at a different problem than ours.

## Penpot

Penpot is more mature than OpenPencil as a complete collaborative design platform. If you want a self-hosted Figma alternative, it is the obvious answer and it is good.

It is also not an embeddable component, and this is an architectural fact rather than a criticism.

Penpot is a ClojureScript frontend backed by a Clojure server, PostgreSQL, WebSockets, and supporting services. Adopting it does not mean adding a dependency. It means **operating a second application** alongside your own — a different language runtime, a different database, a different deployment and upgrade path, a different on-call surface.

For a small team, that is the decisive consideration. The question is never just "is this software good." It is "what am I now responsible for keeping running at 3am."

Penpot is excellent at what it is. It is not a library.

## tldraw

Technically outstanding. Genuinely one of the best canvas engines available, with excellent developer ergonomics and a team that clearly cares about the details.

The blocker is licensing. tldraw's SDK is **source-available, not permissively open source.** You can read it, you can experiment with it, and commercial production use requires a license.

This is a completely legitimate business model and we have no complaint about it. But "open source" gets used loosely enough that plenty of teams adopt tldraw assuming MIT-style terms and discover otherwise at an inconvenient moment. If you are evaluating canvas libraries, read the license before you read the API docs. It is the faster way to shorten the list.

We had picked up some tldraw dependencies during earlier experimentation. Part of this work was removing the remaining runtime and lockfile references.

## Excalidraw

Already our engine, and we kept it.

Excalidraw is the right foundation for Note, Whiteboard, and Slides modes: fast, familiar, permissively licensed, with an element model simple enough to extend and a hand-drawn aesthetic that makes a blank canvas feel inviting rather than intimidating.

What it is not is a precision design tool. No real layer system, no components, no constraints, no auto-layout. That is a deliberate scope choice on their part and the right one for what Excalidraw is for.

We upgraded to 0.18.1 as part of this work.

## Apple Freeform

Closed source, so it cannot be a technical candidate. We include it because it is the most useful **UX benchmark** in the category, and the lesson is worth more than a library would have been.

Freeform supports a boundless canvas with photos, audio, video, PDFs, files, links, drawings, shapes, and stickies. Touch and Apple Pencil first. Real-time collaboration. Effortless device sync.

Its strength is not depth. Figma has vastly more capability. Freeform's strength is **approachability** — you open it and immediately understand what to do, with no tutorial and no wall of tools.

That distinction shaped our own design. Our Whiteboard mode should feel closer to Freeform than to Figma. The advanced capability belongs in Design mode, where someone has explicitly opted into complexity.

## What We Decided

We kept Excalidraw as the single underlying engine for all four modes rather than embedding a second canvas.

The reasoning is about the object model, not the technology. Excalidraw elements and a design tool's document model are structurally different. Any attempt to silently convert content between them produces fidelity loss, and fidelity loss that happens automatically is the worst kind — the user did not ask for it, cannot predict it, and discovers it after the fact.

The right pattern is explicit and user-initiated. A "send to design" action that converts *selected* material, where the person chose to do it and can see the result. Never an automatic background conversion when someone switches views.

## If You Are Making the Same Decision

Four questions, in the order that eliminates candidates fastest:

1. **What is the license, exactly?** Source-available is not open source. Check first.
2. **Is it a library or an application?** If it brings its own database and services, you are adopting an operational burden, not a dependency.
3. **Does the framework match yours?** A framework mismatch is survivable but never free, and the cost is permanent.
4. **Is its roadmap aimed at your problem?** A project can be excellent and still be heading somewhere other than where you need to go.

Our answers pointed at OpenPencil as the eventual design foundation and Excalidraw as the everyday engine. Yours may differ. The questions should not.

[Try the canvas we actually built](https://app.omnicanvasnotes.com/?mode=try&template=brainstorm-web&utm_source=blog&utm_medium=organic&utm_campaign=oss_canvas_eval) — no account required.

30 days free. No credit card required.
`,
    cta: { templateId: "brainstorm-web", templateName: "Brainstorm Web" },
  },

  {
    slug: "never-lose-the-recording",
    title: "Never Lose the Recording: Durability Lessons From One Lost Meeting",
    description:
      "A full meeting was recorded. Stop was pressed. Nothing appeared. Here is what was wrong, and the durability rules we now apply to anything capturing input.",
    date: "2026-07-27",
    readTime: "8 min read",
    category: "Techniques",
    preserveDate: true,
    coverImage: null,
    content: `
## The Bug Report

"I just finished a meeting where I recorded the whole thing. When I clicked stop recording, I don't see the recording saved on the canvas. This was the second recording on this canvas."

That is the worst class of bug in a note-taking product. Not a crash, not a visual glitch — an hour of someone's meeting, gone, discovered only after the meeting ended and everyone left.

The parenthetical at the end turned out to matter a great deal.

## What Was Actually Wrong

The architecture had a sequence problem. Stopping a recording kicked off a pipeline: finalize the audio, hand it to transcription, generate the capture package, place the card on the canvas, persist.

Persistence was near the end of that chain.

Which means any failure anywhere in the middle — a transcription error, a network blip, a timeout, a state collision from a second recording on the same canvas — lost the audio. Not "lost the transcript," which would be annoying. Lost the raw recording, the one irreplaceable artifact in the entire pipeline. Everything downstream can be regenerated. The audio cannot, because the meeting is over.

The second-recording detail exposed a state assumption: the flow had been built and tested for one recording per canvas, and a second one during the same session collided with leftover state from the first.

## Rule One: Persist the Irreplaceable Thing First

The fix that mattered:

> **Recordings are persisted immediately when stopped, before transcription or any AI action runs.**

Stop means stop and save. Full stop. Everything else — transcription, summary, capture package, card placement, upload — happens afterward against durably stored audio, and every one of those steps is allowed to fail without costing the user anything they cannot recover.

This inverts the natural way you write the code. The natural way is a pipeline, because that reads well and matches how you think about the feature. The durable way is: capture, commit, then process. The commit is a hard boundary. Nothing before it is allowed to be optional and nothing after it is allowed to be load-bearing for the raw artifact.

## Rule Two: Save As You Go

Persisting on stop is necessary but not sufficient, because it still assumes the user reaches "stop."

Real sessions do not always get there. The tab crashes. The browser is killed for memory. The laptop sleeps and the page is discarded. The user closes the wrong window. In every one of those cases, an hour of audio held only in memory is an hour of audio that never existed.

So the recording is written incrementally during capture, not assembled at the end. If the session dies at minute 47, there is 47 minutes of recoverable audio rather than nothing.

The general principle applies well beyond audio: **anything that accumulates user input over minutes needs incremental durability.** Long recordings, long forms, long editing sessions. If the value grows with time, so does the cost of losing it, and the probability of an interruption grows right along with it.

## Rule Three: Bound Every Asynchronous Operation

A related failure from the same period: the AI actions modal could not be closed while processing, and AI requests had no timeout.

Combine those and a slow response is indistinguishable from a hang. The user is stuck in a modal they cannot dismiss, watching a spinner, with no way to tell whether waiting will help. The only escape is to reload the page — which, before the persistence fix, could also lose the recording.

Two changes:

- AI requests time out at 45 seconds rather than waiting indefinitely
- The modal stays closable during processing

Neither is clever. Both are the difference between a slow feature and a broken one. A user who can cancel and retry has a slow tool. A user who cannot has a tool that has taken their work hostage.

## Rule Four: Show Progress Early

Live transcription now produces first output at roughly four seconds and refreshes every six.

This is a perception fix rather than a correctness fix, but it does real work. A recorder that shows nothing for thirty seconds is indistinguishable from a recorder that is not working, and the reasonable user response to a tool that appears not to be working is to stop it and start over — which is how you lose a recording to a feature that was functioning perfectly.

Early feedback is a durability feature, because it prevents the user from taking destructive corrective action.

## Rule Five: Test the Second One

The bug report said "this was the second recording on this canvas," and that phrase deserves its own rule.

Most feature testing exercises the first invocation. First recording, first upload, first export. The second one is where leftover state, unreleased handles, stale identifiers, and half-cleaned listeners surface.

For any stateful feature, the minimum test matrix is: first invocation, second invocation in the same session, and invocation after an interrupted previous attempt. The third case is the one that finds the most bugs and the one nobody writes.

## What This Cost to Fix

The recording durability work touched 23 source and test files. That number is worth stating plainly, because "persist earlier in the pipeline" sounds like a one-line change and it never is. Reordering when the durable write happens means every downstream consumer now receives a reference to stored audio rather than an in-memory object, and every one of them has to be updated.

Validation was 1,171 tests, TypeScript, and a production build before it went near main.

## The Rules, Collected

If you build anything that captures user input over time:

1. **Persist the irreplaceable artifact first**, before any processing that could fail
2. **Write incrementally**, because sessions do not always reach a clean stop
3. **Bound every async operation** with a timeout, and never trap a user in a modal
4. **Show progress early** so nobody cancels a working operation
5. **Test the second invocation**, and the one after an interrupted attempt

None of these are sophisticated. All five were violated by code that had passed review and worked correctly in every test we had written.

[Record a meeting on a canvas](https://app.omnicanvasnotes.com/?mode=try&template=meeting-notes&utm_source=blog&utm_medium=organic&utm_campaign=recording_durability) and see where the transcript ends up.

30 days free. No credit card required.
`,
    cta: { templateId: "meeting-notes", templateName: "Meeting Notes" },
  },

  {
    slug: "bugs-that-only-exist-on-a-phone",
    title: "The Bugs That Only Exist on a Phone",
    description:
      "A sync limit that only trips for real users, an iOS login that strands you on a blank page, and a mic permission with no setting to grant it. Five bugs.",
    date: "2026-07-20",
    readTime: "9 min read",
    category: "Techniques",
    preserveDate: true,
    coverImage: null,
    content: `
## Every One of These Passed Desktop Testing

OmniCanvas runs as a web app and as an installed PWA on phones and tablets. Over several weeks we worked through a cluster of bugs that had one thing in common: none of them were reproducible on a development machine, and none of them were caught by our test suite.

Here they are, with what each one turned out to be about.

## 1. "Sync Failed: Too Many Folders in One Sync"

**The report:** sync failing on a phone with a server error about too many folders in a single push.

**What was happening:** the server enforces a per-request limit on how many folders a sync push may contain. Sensible. The client pushed every pending folder in one request. Also sensible, right up until a user has more folders than the limit.

Note the shape of this bug. It is not a mobile bug at all — the same code would fail identically on desktop. It surfaced on a phone because the phone belonged to the person with the most real data. **Limits that scale with usage are discovered by your heaviest users, and your heaviest user is usually you.**

**The fix:** the client now batches pushes at 50 items, for both notes and folders. The test asserts that 120 folders go out as 50, 50, and 20.

The broader lesson is about where a limit gets enforced. A server-side limit with no client-side batching is not a limit, it is a trap that arms itself as a user succeeds with your product. Any paginated or bounded endpoint needs the client to respect the bound, and needs a test at a quantity larger than anyone on the team has locally.

## 2. iOS: Stranded on "You Can Close This Tab"

**The report:** after signing in with Google on the iOS PWA, the user was stuck on a blank screen reading "you can close this tab." Killing the app was the only way out.

**What was happening:** OAuth in an installed PWA on iOS opens an external browser context for the provider. After authentication, that context is supposed to hand control back and close itself. On iOS it does not reliably close, and inside an installed PWA there is no visible browser chrome — no tab bar, no close button, no back gesture that helps. The instruction "you can close this tab" refers to something the user cannot see and has no way to act on.

**The fix:** two layers. The temporary context now closes itself automatically after the handoff. And because that will not work in every iOS version and configuration, there is a visible fallback button that returns the user to the app.

**The rule:** never leave a user on a screen whose only instruction requires interface that is not present. Text like "close this window" or "press back" assumes browser chrome. Installed PWAs do not have it. Every terminal state needs an in-page control.

This bug also has a nasty property: it hits during signup, which means the people affected are new users, and new users who cannot complete signup do not file bug reports. We only heard about it because the report came from someone we knew.

## 3. The Microphone Permission With No Setting

**The report:** recording works in the mobile PWA, but on a MacBook it reports missing permissions — and the option to grant them does not appear in macOS system settings.

This one was genuinely confusing, because the user's mental model was correct and the platform was not cooperating with it. They looked in the obvious place, the entry was absent, and there was nothing else to try.

The behavior differs by platform, by browser, by installation mode, and by whether a permission was previously denied. A permission denied in a browser context can persist in a way that produces exactly this symptom: the app reports no access, and the operating system has no record of the app to show you.

**The fix:** when a permission check fails, do not just report that permission is missing. Explain where the permission actually lives for *this* platform and installation mode, and give the user a direct route there.

Concretely, the failure message now detects the browser by name and says to look for **that browser** in macOS Microphone settings — not OmniCanvas, and not the installed PWA. That is the detail that made the original bug so baffling: the user searched for the app they were using, and the entry that matters is filed under the browser hosting it. If no entry exists yet, the message explains that microphone access has to be allowed at the site level first, because the operating system does not list an app that has never requested the permission.

There is also a direct link into the macOS Privacy settings pane, and the message varies by what actually failed — no input device found, screen-share audio blocked, or microphone blocked are three different problems with three different next steps.

"Microphone access denied" is a true statement and a useless one. The user already knows something is wrong. What they need is the specific next action on the specific machine they are holding.

## 4. The Toolbar You Could Not Drag

**The report, part one:** on mobile, dragging the canvas toolbar just pushed it to the bottom until it got stuck and became unusable.

**The report, part two:** on desktop, dragging did not track the pointer — the toolbar jumped to a position centered on the cursor and about a hundred pixels above the toolbar itself.

Two symptoms, one root cause: drag was implemented against mouse events, with touch handling added separately and inconsistently, and the drag origin was calculated from the element rather than from the grab point.

**The fix pattern:** use Pointer Events with pointer capture, which unifies mouse, touch, and stylus into one code path. Then calculate the drag offset from where the pointer actually landed within the element, so the grabbed point stays under the pointer.

That second part is what "it jumps" almost always means. If a dragged element snaps so its center is at the cursor, the code is setting position from pointer coordinates without subtracting the initial offset within the element. It looks fine when you grab something near its center, which is what you do when testing your own feature.

## 5. Shrinking the Window Made Controls Disappear

**The report:** making the desktop window smaller lost UI elements entirely.

The layout had a desktop arrangement and a mobile arrangement, selected by viewport width. A narrow desktop window fell into a gap — too narrow for the desktop layout to fit, wide enough that the mobile layout did not engage. Controls overflowed their container and were clipped rather than reflowed.

**The insight, which came from the user:** a narrow desktop window should just use the mobile interface. The mobile layout already solves the narrow-viewport problem properly, with a drawer and a bottom sheet. There is no reason to maintain a third arrangement for the awkward middle.

We took this further later, consolidating the canvas menu into a single contextual drawer so there was one place controls lived rather than a native menu plus an app menu that disagreed with each other at narrow widths.

**The rule:** breakpoints should be chosen by whether the layout fits, not by whether the device is nominally a phone. A 500-pixel-wide desktop window has the same constraints as a phone and should get the same treatment.

## What Ties These Together

Four of the five were invisible on a development machine — wide window, fast network, mouse input, permissions already granted, small test dataset.

The mitigations that actually work are unglamorous:

- **Pointer Events for anything draggable.** Not mouse events with touch bolted on.
- **Client-side batching for every bounded endpoint**, tested above the bound.
- **Breakpoints chosen by fit**, not by device category.
- **No terminal state that depends on absent browser chrome.**
- **Permission failures that name the platform-specific place to fix it.**

And the one that finds the rest: use the product on a phone, with your real data, on a real network, as your primary device for a week. Every bug above was found that way. None were found by testing.

[Open OmniCanvas on your phone](https://app.omnicanvasnotes.com/?mode=try&template=brainstorm-web&utm_source=blog&utm_medium=organic&utm_campaign=mobile_bugs) and see how it holds up.

30 days free. No credit card required.
`,
    cta: { templateId: "brainstorm-web", templateName: "Brainstorm Web" },
  },

  {
    slug: "space-to-pan-canvas-keyboard-focus",
    title: "Space to Pan, Except When You Are Typing: Keyboard Focus on an Infinite Canvas",
    description:
      "Pressing space should pan the canvas — unless there is a text cursor, when it should type a space. Harder than it looks, and everyone gets it wrong.",
    date: "2026-07-24",
    readTime: "6 min read",
    category: "Techniques",
    preserveDate: true,
    coverImage: null,
    content: `
## The Report

"I clicked on the empty canvas and then pressed space so I could pan around, but it is keeping the cursor inside a text box and adding spaces."

Small bug. Two-sentence report. It took longer to reason about correctly than several features shipped the same week, and it belongs to a family of problems that every canvas application eventually hits.

## Why Space Is a Uniquely Bad Key

Space-to-pan is a near-universal convention. Figma, Photoshop, Illustrator, Miro, every serious canvas tool. Users arrive expecting it.

Space is also the most common character in written text.

So the same keystroke means two incompatible things, and the only thing distinguishing them is whether the application considers a text editor to be active. Every other canvas shortcut has an escape hatch — you can pick a different letter, add a modifier, use a tool palette. Space has no alternative, because the convention is too strong to break.

That makes the focus question load-bearing in a way it is not for any other key.

## Why "Is a Text Box Focused" Is Not a Simple Question

The naive check is to ask whether the active element is an input or a textarea. On a canvas, this is not enough.

Canvas text editing frequently is not a real form element. It may be a contenteditable region, an overlay positioned above the canvas, or a text editing mode maintained entirely in application state while document focus stays on the canvas element itself. In that last case — which is common in canvas engines — the document reports the canvas as focused while the user is very much in the middle of typing.

There is also the reverse case, which is what produced this bug. The user clicked empty canvas, believing they had left the text box. Visually, they had. But the editing state had not been torn down, so the application still considered a text element active, and space went to the text.

Two ways to be wrong:

- **Type when the user meant to pan.** Annoying. The canvas does not move, a stray character appears somewhere, undo fixes it.
- **Pan when the user meant to type.** Much worse. The canvas lurches, the word is broken, and the user has to work out what happened.

The asymmetry matters when you decide which way to fail.

## What We Actually Fixed

Two separate things, and they needed different treatment.

**The state was stale.** Clicking empty canvas should end text editing. It looked like it had — the caret was not visible — but the underlying editing state persisted. The visual and the logical state disagreed, and the keyboard handler correctly consulted the logical one.

The general form of this bug: any time a user performs an action that visually exits a mode, the mode must actually exit. A caret that stops rendering while editing state remains active will produce a keyboard bug eventually, and it will be reported as something unrelated.

**The check needed to be authoritative.** Rather than inferring editing state from document focus, the handler consults the canvas engine's own notion of whether an element is being edited, plus the standard editable-element check for overlays and inputs. One source of truth, checked before any canvas shortcut runs.

## The Related Rule We Already Had

This sits next to a rule we learned earlier and painfully: **never call a scene update while a text element is being edited.**

That one came from real-time collaboration. An update arriving from another device would refresh the scene, and if the local user was mid-word, the refresh would clobber the in-progress text — dropping typed characters as they were typed. On a phone with an on-screen keyboard, it was maddening.

The fix was a guard on the editing element before any scene update, and it is the same underlying principle as the space bug: **an active text editor is a protected region.** Nothing global — not a shortcut, not a remote update, not a re-render, not a viewport change — gets to act on the canvas while a user has a cursor in a text element.

## The Checklist

If you maintain a canvas application:

1. **One authoritative editing-state check**, consulted by every global handler. Not document focus alone.
2. **The check covers all three cases**: form elements, contenteditable regions, and engine-internal text editing.
3. **Clicking away genuinely ends editing**, so the visual and logical states never diverge.
4. **Remote or asynchronous updates are deferred** while editing is active.
5. **When ambiguous, favor typing.** Inserting a stray space is recoverable with one undo. Panning the canvas mid-word is disorienting and there is nothing obvious to undo.

Point five is the one to internalize. The two failure modes are not equally bad, so when the state is genuinely unclear, fail toward the cheaper mistake.

## Why It Is Worth the Attention

Nobody praises a canvas app for handling the space bar correctly. It is invisible when right.

When wrong, it undermines confidence in the whole tool. A user who gets stray spaces in their notes, or whose canvas jumps while typing, stops trusting the keyboard. They start reaching for the mouse for everything, and the product feels slow in a way they will never be able to articulate in a bug report.

Keyboard handling is one of those areas where the entire return on the work is the absence of a complaint.

[Try it on a real canvas](https://app.omnicanvasnotes.com/?mode=try&template=brainstorm-web&utm_source=blog&utm_medium=organic&utm_campaign=space_to_pan) — click empty space, hold space, drag.

30 days free. No credit card required.
`,
    cta: { templateId: "brainstorm-web", templateName: "Brainstorm Web" },
  },

  {
    slug: "why-we-refused-points-badges-streaks",
    title: "Why We Refused to Add Points, Badges, and Streaks",
    description:
      "The three mechanics every app adds first are the three most documented failures. We built a small open loop tied to whether a meeting produced a decision.",
    date: "2026-07-26",
    readTime: "8 min read",
    category: "Insights",
    preserveDate: true,
    coverImage: null,
    content: `
## The Prompt

We were looking at gamification because our retention numbers were bad — 17% of activated users edited a note after day one, 8% after day seven.

Gamification is the obvious lever. Points, badges, leaderboards, streaks. Every productivity app has them and they are well understood.

They are also, by a wide margin, the most documented failures in the category. The three mechanics teams reach for first are the three with the longest record of not working. So before adding any of them, we tried to work out what the successful implementations were actually doing, because it is clearly not "points."

## The Reframe

The useful version is not "add better gamification." It is:

> **Make progress toward the user's real outcome visible, easy to finish, and satisfying to close.**

Every part of that sentence is doing work. *The user's real outcome*, not a proxy for it. *Visible*, so they can see where they are. *Easy to finish*, so the loop closes. *Satisfying to close*, so closing it feels like something.

Notice what is absent: any external reward. The mechanism is a small open loop tied to work the person already wanted to do.

## Naming Our Actual Outcome

For OmniCanvas, the outcome is not "create notes" and it is definitely not "open the app." Those are our metrics, not the user's goals. Nobody has ever wanted to have created a note.

The real outcome is **turning captured material into decisions and next actions.** Somebody records a meeting because they want the meeting to produce something. The recording is instrumental. The decision is the point.

Once that is written down, the design follows.

## What We Built

A lightweight per-meeting "close the loop" flow with four steps:

1. Recording captured
2. Transcript ready
3. Recap and action items reviewed
4. Next step checked off or added to the calendar

The capture package shows something like **"Meeting closed: 3/4."** Steps can be skipped when irrelevant. The whole mechanic can be dismissed. Completion gets a subtle acknowledgment and then gets out of the way.

The progress indicator is not the interesting part — it is the most replaceable element of the design. What matters is that it is a **small, achievable, dismissible open loop attached to real work.** Three of those four adjectives are usually missing from gamification implementations.

The other thing worth noting: we already had most of it. The product generates action-oriented capture packages with checkboxes, places them beside recordings automatically, and supports interactive checklists in the editor. This was assembling an existing workflow into a visible loop, not building a gamification system. That is generally a sign you are on the right track — the mechanic should be revealing structure that is already there, not imposing structure that is not.

## What We Explicitly Refused

**Points for notes, recording minutes, or AI operations.** These reward volume. Volume is not the outcome, and rewarding it produces users who create more notes without getting more value — which shows up in our metrics as improvement while the user's actual experience gets slightly worse.

**Badges for opening the app or creating a lot of content.** Same problem, with a collectible attached.

**Daily streaks.** This is the one we feel most strongly about. Serious note-taking is **episodic**. You have a week of meetings and then a week of heads-down work. A streak counter punishes the second week, which is often the more productive one. Worse, streaks generate anxiety about the tool rather than engagement with the work, and a broken streak is a well-documented reason people abandon an app entirely. Building a mechanic whose failure state is churn is a strange thing to do on purpose.

**Leaderboards between knowledge workers.** Ranking colleagues by note count is a bad idea in ways that should not need explaining.

**Loot-box-style variable rewards.** Variable reward schedules are effective at driving compulsive use, and they are profoundly mismatched with a tool people are trusting with their work. If someone is deciding whether to put confidential meeting notes in your product, the last impression you want to create is that it is designed to be compulsive.

## Surprise, Done Honestly

Variable rewards do address something real: predictable systems get boring. The question is what the surprise consists of.

The wrong answer is confetti, random bonuses, or artificial scarcity. The right answer is **intrinsically useful discovery**:

- "We found a related decision from last month"
- "This unresolved question has appeared in three meetings"

That is genuinely surprising, genuinely variable, and the reward is information the user actually wanted. It cannot be gamed, because generating more notes does not generate more insights — only relevant ones do.

## Competence Feedback Beats Achievement

This is the biggest opportunity and the one furthest from conventional gamification.

After enough usage, show evidence that the user's system is working:

- "4 of 5 meetings ended with a clear next step"
- "7 action items completed"
- "3 open questions resolved"
- "This note contributed to two later decisions"

Compare that to "you created 100 notes."

The first set demonstrates **effectiveness**. It tells someone their process is producing results, which is information they want and cannot easily get anywhere else. The second is badge theater — it tells them they used a tool a lot, which they already knew and which proves nothing.

For collaboration, acknowledgment can work the same way: a teammate confirms a decision, accepts an action item, resolves an open question, or says thanks. Grounded in the work. No ranking required.

## The Metric We Flagged as Wrong

One honest note. Our current definition of "meaningful work" leans on object count and text length — did you place enough things or type enough characters.

That is a volume heuristic, and this entire post is an argument against volume heuristics. It is on the list to replace with something intent-shaped: did the user do something that implies they want to keep this? Naming it. Structuring it. Returning to it.

We shipped the weak version because it was ready. Writing down why it is wrong is how it gets fixed.

## The Short Version

Do not ask "what mechanic should we add." Ask "what is the user actually trying to finish, and can they see how close they are."

If the answer to the second question is yes, you may not need a mechanic at all. If it is no, no amount of points will help — you will just be measuring the wrong thing more enthusiastically.

[Open a meeting-notes canvas](https://app.omnicanvasnotes.com/?mode=try&template=meeting-notes&utm_source=blog&utm_medium=organic&utm_campaign=no_gamification) and see what closing the loop looks like.

30 days free. No credit card required.
`,
    cta: { templateId: "meeting-notes", templateName: "Meeting Notes" },
  },

  {
    slug: "what-ipad-note-taking-reviews-agree-on",
    title: "We Watched Eight iPad Note-Taking Reviews. Here Is What They All Agree On.",
    description:
      "Eight reviewers, eight different top picks, and real consensus underneath. What the iPad note-taking category actually agrees about in 2026.",
    date: "2026-07-30",
    readTime: "9 min read",
    category: "Reviews",
    preserveDate: true,
    coverImage: null,
    content: `
## The Method

We went through eight iPad note-taking review videos from 2026 — the roundups, the five-best lists, the multi-year power user retrospectives, the "is Apple Notes actually enough" explainers.

They disagree on almost every recommendation. One picks Notability, another dismisses it. One says GoodNotes is the obvious default, another says its recent direction has alienated long-time users. Every video has a different number one.

But underneath the disagreement, the same points recur with real consistency. Those points are more useful than any individual ranking, because a recommendation tells you what one reviewer likes and a consensus tells you what the category has actually figured out.

## 1. There Is No Best App, and the Good Reviewers Say So

The most-repeated line across all eight: the right app depends on how you work, not on a feature checklist.

The framing that recurs is by *problem*, not by app. Handwriting-first. PDF markup and annotation. Typed notes with structure. Planning and journaling. Research and synthesis. Reviewers who organize their video this way produce advice that survives contact with a real reader. Reviewers who rank apps one through five produce a list that is obsolete in six months.

Worth noting for anyone writing in this space: the honest framing is also the more useful one. "It depends on what you need" is a cliché but it is a cliché because it is true, and the videos that lean into it are the ones with the most engaged comment sections.

## 2. The App Is Not What Makes You Good at Notes

Several reviewers, especially the ones with years in the category, land on the same conclusion: the biggest lie in note-taking is that the right app will make you a better note-taker.

One put it directly — six years of iPad note-taking, and the thing that mattered was not the app. It was having a process for turning captured material into something usable.

This is the most important point in all eight videos and it is the one that appears last in each of them, after the recommendations. The apps are largely interchangeable at the capture layer. Handwriting works everywhere now. The differences that matter show up in what happens *after* capture, and most apps have very little to say about that.

## 3. Handwriting Is Table Stakes, and It Is Not a Differentiator

Every serious app has good Apple Pencil support, palm rejection, pressure sensitivity, and reasonable ink rendering. Reviewers still demo it, but nobody is choosing based on it anymore.

Two things do still differentiate: **handwriting search and conversion quality**, and **PDF annotation depth** for people whose work is mostly marking up documents. If you're evaluating that first point, it's worth testing a page of your own handwriting through a tool like our free [handwriting to text converter](/tools/handwriting-to-text) rather than trusting a spec sheet.

The rest is settled. If an app's pitch is that its ink feels good, it does not have a pitch.

## 4. Search Is the Feature People Regret Not Checking

A recurring pattern in the retrospective videos: someone accumulates two years of handwritten notes, then cannot find anything.

Handwriting recognition quality, whether search covers PDFs and images, whether it works across notebooks rather than within one — these get mentioned as the thing people wish they had evaluated at the start and did not, because on day one you have four notes and search seems irrelevant.

Anyone evaluating a note app should test search against a realistic corpus, not four test notes. The behavior at four notes tells you nothing about the behavior at four hundred.

## 5. Organization Systems Get Abandoned

Reviewers who show their real setups almost always admit their folder hierarchy collapsed. Elaborate nesting built in month one, abandoned by month three, replaced by search and a handful of pinned items.

The consistent advice: prefer flat structure plus good search over deep hierarchy. Tags over folders. Whatever requires the least maintenance, because a system that requires maintenance will not get it.

This matches what we see in our own data, incidentally. Of 57 new accounts we analyzed in July, four used folders and two used tags. Organization features are not what new users reach for, and elaborate organization is a thing experienced users retreat *from*, not toward.

## 6. Lock-In Is a Real and Recurring Complaint

Proprietary formats come up constantly, usually with irritation. Notes trapped in a format only one app reads. Export that loses annotations or reflows layout. Subscription changes that alter the terms after years of accumulated content.

The advice that recurs: check what export produces *before* you commit, not when you want to leave. And check what happens if you stop paying — whether you retain read access or lose the notes entirely.

For a category where people accumulate years of material, this is a rational thing to be nervous about, and reviewers have started treating it as a first-class evaluation criterion rather than a footnote.

## 7. Apple Notes Is Better Than People Expect

Multiple videos ask whether Apple Notes is enough, and the answers are more generous than they were a few years ago. It is free, synced, fast, searchable, and handles handwriting.

The consensus limits: weak PDF workflows, no infinite canvas, limited structure for research or synthesis, and organization that does not scale to large collections.

The reasonable framing several reviewers use — start with Apple Notes, and switch when you hit a specific wall you can name. If you cannot name the wall, you do not need a different app, and buying one will not help.

## 8. Nobody Is Excited About AI Features, Yet

AI shows up in most of these videos as a bullet point rather than a reason to switch. Summarization, handwriting cleanup, transcription. Demoed, acknowledged, and rarely decisive.

The skepticism is fair. Most implementations are a summarize button attached to a note, and a summary of a note you just wrote is not obviously valuable. The AI features that seem to land are the ones addressing something the user genuinely cannot do themselves — transcribing a meeting they were participating in, pulling structure out of an hour of audio, finding a connection across notes written months apart.

The category has not settled this yet. Anyone claiming AI note-taking is a solved value proposition is ahead of the evidence.

## What We Took From It

Three things we are acting on:

**Search across a large corpus is the retention feature.** People leave when they cannot find things, and they discover this long after switching costs are high.

**Post-capture is where the actual competition is.** Handwriting is solved. What happens to a note after it exists is not.

**Say plainly what you are not.** The reviews are consistently harsher on apps that overclaim than on apps with a narrow scope honestly described. A tool that says "this is for PDF markup and not much else" gets a better reception than one that claims to do everything.

That last one is why our own product is organized as modes with explicit names and explicit scopes rather than one interface claiming to be everything at once.

[Try a spatial canvas on your iPad](https://app.omnicanvasnotes.com/?mode=try&template=cornell-notes&utm_source=blog&utm_medium=organic&utm_campaign=ipad_reviews) — it installs as an app, no App Store required.

30 days free. No credit card required.
`,
    cta: { templateId: "cornell-notes", templateName: "Cornell Notes" },
  },

  {
    slug: "when-your-seo-audit-flags-the-wrong-thing",
    title: "Our SEO Audit Said Our JavaScript Was Broken. It Wasn't. The Real Problem Was Worse.",
    description:
      "An automated crawl reported broken JavaScript on two pages. We could not reproduce it, so we did not fix it — but we found 192 URLs quietly redirecting.",
    date: "2026-07-25",
    readTime: "6 min read",
    category: "Insights",
    preserveDate: true,
    coverImage: null,
    content: `
## The Alert

An automated site audit emailed us: **"Page has broken JavaScript — 2 URLs."**

That is the kind of alert that produces an immediate urge to go change something. Broken JavaScript on a site that is entirely a JavaScript application sounds serious, and there is a strong pull toward finding a plausible culprit and shipping a fix.

We resisted that, and it was the right call.

## Reproducing It First

Before changing anything, we tried to reproduce the finding. That meant crawling the site the way the auditor does — all 194 live HTML pages, using the auditor's own user agent, checking every referenced script.

Results:

- Zero page failures
- Zero JavaScript failures
- Every page referenced the same current hashed bundle
- That bundle returned 200 with the correct content type

The finding was not reproducible on production.

## What Had Actually Happened

The timing lined up with a deployment.

Our site is a static build with content-hashed asset filenames. Every deploy produces a new bundle hash and new HTML referencing it. During the turnover window, a crawler can fetch an HTML page from the old deployment and then request its script after the new deployment has replaced it. The page is real. The script reference is real. It just no longer exists by the time the crawler asks for it.

Result: a broken-JavaScript report for a site that was never broken for any human visitor, whose page load and script fetch happen within milliseconds of each other rather than across a crawl queue.

The exact two URLs were only visible inside the authenticated audit report, which meant we could not check them directly — another reason to verify by independent crawl rather than trust the summary.

## The Decision Not to Fix It

We made no code changes. This deserves saying explicitly, because "the audit said X, so we changed Y" is an extremely common and mostly harmful pattern.

Changing code to satisfy an unreproducible alert has a poor risk profile. You have not fixed a known problem, because there is no known problem. You have added a change, and changes carry risk. Meanwhile the alert clears on the next crawl regardless, and you now believe your change did it — which means the next time it appears you will make another unnecessary change.

The correct response to an unreproducible finding is to document why it is not reproducible and rerun the crawl. If it recurs on a stable deployment, it is real and worth investigating properly.

There is a legitimate underlying question here about whether stale asset references during deployment turnover matter at all. For human visitors, essentially never — the window is seconds and the failure requires fetching HTML and JS on opposite sides of it. For crawlers with queued requests, occasionally. It is a known characteristic of hash-based cache-busting deployments, not a defect.

## The Thing We Actually Found

The crawl we ran to disprove the alert surfaced a genuine problem the alert had not mentioned.

**Every non-root sitemap URL was redirecting.** Our sitemap generator appended a trailing slash to each path. Our server serves those paths without one — a request for /pricing returns 200, and a request for /pricing/ returns a 307 to /pricing.

So every entry in the sitemap pointed at a redirect, and the sitemap disagreed with the canonical tag the pages themselves emitted.

This is a substantially bigger deal than the reported issue:

- Every sitemap entry costs a redirect hop before reaching real content
- The sitemap and the canonical tags were telling crawlers two different things
- Crawl budget goes to redirects instead of pages
- It affects the entire sitemap, not two pages

And unlike the JavaScript alert, it was completely reproducible and unambiguously wrong.

## A Footnote On Getting This Backwards

Our first write-up of this finding said the opposite: that the sitemap and canonicals were missing a trailing slash *the site required*. That was wrong, and it survived for weeks because it was never checked against the server.

The correction took two commands — request a URL with the slash, request it without, compare status codes. That is all it takes to determine which form a site actually canonicalizes, and it is worth doing before writing a line of code, because a fix built on the reversed diagnosis would have added trailing slashes everywhere and pointed all 217 canonical tags at redirects instead.

Which is a more expensive version of the same mistake this post is about: acting on a plausible description of a problem rather than a verified one.

## Why the Audit Missed the Bigger Problem

Automated audits are pattern matchers. They are good at finding the patterns they know and they report those patterns with equal confidence regardless of severity. A transient script fetch failure and a systematic canonical URL mismatch both arrive as findings in a list.

The severity ranking has to come from you, because the tool does not know that one of those affects two pages transiently and the other affects your entire sitemap permanently.

What we would now do with any audit report:

1. **Reproduce independently** before changing anything. Use the tool's user agent, crawl it yourself.
2. **Check the deployment timeline.** Transient findings clustering around a deploy are usually about the deploy.
3. **Count the blast radius.** Two URLs versus 192 URLs should determine the order of work, not which alert had a scarier name.
4. **Pay attention to what you find while investigating.** The reproduction crawl is often more informative than the original report.
5. **Do not ship a speculative fix.** An unreproducible finding plus a code change equals an unverifiable outcome.

## The Broader Point

Automated tooling is genuinely useful and we keep running it. But an alert is a hypothesis, not a diagnosis. The gap between them is a reproduction attempt, and that attempt is where the real findings tend to show up.

We spent an afternoon proving that a reported problem was not a problem. That afternoon also produced the single highest-value SEO fix on our list. Neither outcome would have happened if we had simply patched something and marked the ticket done.

[See the site the crawler was arguing about](https://app.omnicanvasnotes.com/?mode=try&template=brainstorm-web&utm_source=blog&utm_medium=organic&utm_campaign=seo_audit) — or just try the product.

30 days free. No credit card required.
`,
    cta: { templateId: "brainstorm-web", templateName: "Brainstorm Web" },
  },

  {
    slug: "ga-realtime-showed-zero-users",
    title: "Google Analytics Showed 22 Users, Then 0. We Spent an Afternoon Proving Nothing Was Wrong.",
    description:
      "Realtime analytics kept dropping to zero. We checked the tag manager, error handlers, headers and endpoints. Here is the investigation, and the dead end.",
    date: "2026-07-10",
    readTime: "6 min read",
    category: "Insights",
    preserveDate: true,
    coverImage: null,
    content: `
## The Symptom

Google Analytics realtime was showing periods with a healthy number of active users, then periods with none. The pattern repeated. It looked like something was intermittently breaking.

When your own analytics look broken, everything downstream becomes untrustworthy — you cannot evaluate a marketing change, a deploy, or a funnel experiment. So it was worth an afternoon.

## What We Checked

**Where the tag comes from.** Analytics is not hardcoded anywhere in our application. It is injected by our edge tag manager. That immediately narrows the search: if the tag is not in our bundle, our bundle is unlikely to be breaking it, though it could still block it.

**Whether the tag manager is reachable.** We fetched the tag manager script directly on both the marketing domain and the app subdomain. Both served correctly, and both returned a valid analytics configuration with the same measurement ID.

**Whether our error handling was interfering.** This was the most plausible internal suspect. We have global error handlers that suppress console noise from third-party scripts. If one of those were doing more than suppressing logs — cancelling an event, swallowing a rejection that mattered — it could break the tag while looking innocent.

We read them. They suppress logging only. They do not prevent execution and do not cancel anything.

**Whether headers were blocking it.** No content security policy or header rule in the repository blocks either the tag manager or the analytics endpoint.

**Whether the site was intermittently failing.** We ran repeated synthetic checks:

- Marketing domain: 20 of 20 requests returned 200
- App domain: 20 of 20 returned 200
- Tag manager script on both domains: 20 of 20 returned 200
- Marketing routes — home, pricing, blog, tools, demo, sitemap, robots: all 200
- App shell, manifest, favicon: 200. The authenticated API endpoint returned the expected 401 when unauthenticated
- Both production builds passed

No intermittent failures.

## The Most Likely Explanation

The screenshot may simply be normal, and this is the part worth internalizing.

Realtime analytics reports over different windows. A "last 30 minutes" count of 22 is entirely compatible with a "last 5 minutes" count of 0. Traffic arrived in a burst 6 to 30 minutes ago, and nobody is on the site right now. Both numbers are correct and they describe different things.

For a site with our traffic profile — a few hundred sessions a day, arriving unevenly — realtime is *expected* to alternate between some users and no users. It is not a smooth line. At low volume, realtime analytics is closer to a stream of individual arrivals than a rate.

Realtime is also the noisiest report available. Ad blockers, privacy-focused browsers, tracking protection, and consent restrictions all suppress events. A meaningful share of visitors never appear in realtime at all, and that share varies with where the traffic came from — technical audiences block far more than general ones.

## What We Could Not Verify

Two things, stated plainly because an investigation that reports only what it proved is not an honest investigation.

**The tag manager dashboard configuration.** Everything we could inspect from code and from the live site was correct, but the configuration itself lives in a dashboard. The things to check there — whether analytics is enabled for both domains, whether the trigger covers single-page-app navigation as well as initial pageview, whether any consent or rule filter intermittently excludes traffic — cannot be verified from a repository.

The single-page-app trigger is the one most likely to cause real problems. If only initial pageviews fire and in-app navigations do not, a visitor who lands and browses registers one event and then vanishes, which produces exactly the sparse realtime pattern we were looking at.

**Production error telemetry.** We wanted to query our own client error logs. The API token we had was not authorized for that database. So we could not rule out intermittent client-side errors from real user sessions.

That is a gap, and the honest conclusion is that we found no evidence of a problem rather than that we proved there is none. Those are different claims and the difference matters. Reporting "everything is fine" when you could not check one of the most relevant sources would have been wrong.

## What We Took Away

**Know the shape of your own traffic before diagnosing your analytics.** At low volume, realtime alternating between some and none is the expected behavior, not a symptom.

**Read the windows on the report.** "Last 30 minutes" and "last 5 minutes" disagreeing is not a contradiction.

**Rule out your own code first, and read the handler rather than assuming.** Our error suppression looked suspicious and was innocent. It took five minutes to confirm.

**Fix credential access before you need it.** The most valuable check in this investigation was the one we could not run. An expired or under-scoped token is not a problem until it is the thing standing between you and an answer.

**Say what you could not verify.** An investigation with a stated gap is more useful than a confident all-clear, because the reader knows where to look if the symptom recurs.

[Try the product the analytics were arguing about](https://app.omnicanvasnotes.com/?mode=try&template=brainstorm-web&utm_source=blog&utm_medium=organic&utm_campaign=ga_realtime) — no account required.

30 days free. No credit card required.
`,
    cta: { templateId: "brainstorm-web", templateName: "Brainstorm Web" },
  },

  {
    slug: "photograph-your-notebook-ocr",
    title: "Photograph Your Notebook, Get Searchable Text",
    description:
      "Handwritten pages are useless to search until they are text. How OmniCanvas scans a notebook page from your phone and makes the writing findable.",
    date: "2026-07-09",
    readTime: "6 min read",
    category: "Tutorials",
    preserveDate: true,
    coverImage: null,
    content: `
## The Problem With Paper

Paper notebooks are excellent for thinking and terrible for finding.

Most people who take notes seriously use both paper and a digital tool, and the split is not accidental — paper is faster, has no notifications, and does not autocomplete your thoughts. The cost shows up months later, when you know you wrote something down and have no way to locate it except flipping pages.

The fix is not to abandon paper. It is to make the paper searchable. If you want to see the underlying model work on a single page before touching the app, our free [handwriting to text tool](/tools/handwriting-to-text) runs the same conversion in your browser.

## What OmniCanvas Does

Two entry points, both landing in the same place:

**Scan notebook page** — opens the camera, takes the photo, places it on the canvas, and runs text extraction immediately.

**OCR from gallery** — uses an existing photo, places it on the canvas, and extracts the text immediately.

Both are available from the create menu, the focus view add menu, and the command palette. The extracted text is saved with the note and is included in full-text search.

The important detail: **the image stays.** OCR does not replace your handwriting with a text approximation of it. You keep the photograph of the actual page — the layout, the arrows, the marginalia, the diagram you sketched — and the extracted text rides alongside it as searchable metadata.

This matters because handwriting recognition is imperfect and always will be. If OCR replaced the image, every recognition error would be permanent data loss. Because it does not, an error is a search miss on one term, and the original is right there to read.

## Getting Better Results

Recognition quality depends more on the photograph than on the software.

**Light it evenly.** The most common failure is a shadow across the page, usually from your own hand or head. Diffuse light beats bright light. A window works better than an overhead lamp.

**Get the page flat.** Curvature near the spine distorts letters. Press the page down or photograph one side at a time.

**Shoot straight on.** An angled photo produces skewed text. Get roughly parallel to the page.

**Fill the frame.** More pixels per character is the single biggest quality lever. Photograph one page, not a whole spread.

**Do not use flash.** It blows out highlights and casts hard shadows from the pen indentations.

Rotate and crop tools are available after capture, so an imperfect photo can be corrected before extraction rather than reshot. The same lighting and framing advice applies if you'd rather run a photo through the standalone [handwriting OCR tool](/tools/handwriting-to-text) than scan from inside the app.

## What Recognizes Well and What Does Not

Being straightforward about this is more useful than a marketing claim.

Reliable:

- Clearly separated print handwriting
- Standard notebook layouts with horizontal lines of text
- Headings, bullet points, numbered lists
- Dark ink on light paper

Less reliable:

- Connected cursive, especially fast cursive
- Text at angles or wrapped around a diagram
- Overlapping annotations and interlinear corrections
- Light pencil, or ink close in tone to the paper
- Symbols, equations, and shorthand

The practical consequence: OCR gets you searchability, not a transcript. You will be able to find the page. You may still need to read the image to get the detail. That is a large improvement over not being able to find it at all, and it is worth knowing before you expect a clean transcription.

If a specific page really needs to be accurate text, extract it and then correct it — editing recognized text is much faster than typing it fresh.

## A Workflow That Sticks

The version that survives contact with real life:

1. **Batch weekly.** Scanning each page as you write it never lasts. Fifteen minutes on Friday does.
2. **One canvas per notebook or per project.** Pages land spatially in order, and you get a visual index instead of a list of filenames.
3. **Add a few keywords per page after scanning.** OCR catches the words you wrote. It does not catch the topic you were thinking about. Two or three typed terms per page dramatically improve later retrieval.
4. **Put the digital and the paper together.** A canvas holding scanned notebook pages next to typed notes, clipped links, and meeting transcripts is the point. Separate systems for paper and digital reproduce the original problem.

Step three is the one people skip and the one that does most of the work. Recognition finds literal words. Retrieval usually starts from a concept.

## Why the Canvas Matters Here

A scanned page in a folder is a file. A scanned page on a canvas is an object you can put things next to.

You can draw an arrow from a sketch in your notebook to the typed decision it turned into. You can cluster four pages from four different meetings that turned out to be about the same problem. You can put a photographed whiteboard next to the transcript of the conversation that produced it.

That is the argument for spatial notes generally, and scanned handwriting is one of the clearest cases for it — because the spatial arrangement on the original page was carrying meaning, and a system that flattens it to a text file throws that away.

## Getting Started

Open a canvas, use **Scan notebook page**, and photograph one page you have been meaning to find again. It will be on the canvas, extracted, and searchable in a few seconds.

Then scan the rest of that notebook. The value of this compounds — one scanned page is a photograph, and two hundred scanned pages is an archive you can search.

[Open a canvas and scan a page](https://app.omnicanvasnotes.com/?mode=try&template=cornell-notes&utm_source=blog&utm_medium=organic&utm_campaign=notebook_ocr) — no account required.

30 days free. No credit card required.
`,
    cta: { templateId: "cornell-notes", templateName: "Cornell Notes" },
  },
];
